import React, { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FONT_FAMILY, COLORS } from "../theme";

interface LoadRowProps {
    value: string;
    onChange: (v: string) => void;
    onLoad: () => void;
    loading?: boolean;
    infoText?: string;
    placeholder?: string;
    toggle?: boolean;
    onToggle?: () => void;
    error?: boolean;
    showInfoIcon?: boolean;
}

export const LoadRow: React.FC<LoadRowProps> = ({ value, onChange, onLoad, loading = false, infoText = "Local directory path with .png images to place on map.", placeholder, toggle, onToggle, error = false, showInfoIcon = false }) => {
    const [showTooltip, setShowTooltip] = React.useState(false);
    const [showToggleTooltip, setShowToggleTooltip] = React.useState(false);
    const infoIconRef = useRef<HTMLSpanElement>(null);
    const toggleRef = useRef<HTMLButtonElement>(null);
    const [isLoadAnimating, setIsLoadAnimating] = useState(false);
    const [isToggleAnimating, setIsToggleAnimating] = useState(false);

    return (
        <div style={{
            width: '95%',
            maxWidth: 900,
            display: 'flex',
            alignItems: 'center',
            marginBottom: 24
        }}>
            <button
                style={{
                    marginRight: 12,
                    fontSize: 16,
                    padding: '6px 18px',
                    borderRadius: 4,
                    fontWeight: 600,
                    background: loading ? '#888' : COLORS.focusBorder,
                    color: COLORS.background,
                    border: 'none',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    opacity: loading ? 0.7 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                }}
                onClick={() => {
                    setIsLoadAnimating(true);
                    onLoad();
                }}
                disabled={loading}
                className={isLoadAnimating ? 'icon-collapse-animate' : ''}
                onAnimationEnd={() => setIsLoadAnimating(false)}
            >
                {loading ? (
                    <>
                        <div style={{
                            width: '16px',
                            height: '16px',
                            border: `2px solid ${COLORS.background}`,
                            borderTop: `2px solid transparent`,
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite'
                        }} />
                        Loading...
                    </>
                ) : (
                    'Load'
                )}
            </button>
            <input
                type="text"
                value={value}
                onChange={e => onChange(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') onLoad(); }}
                placeholder={placeholder}
                style={{ fontSize: 16, padding: '6px 14px', borderRadius: 4, width: '100%', minWidth: 0, flexGrow: 1, background: loading ? '#eee' : undefined, border: error ? '2px solid #e74c3c' : '1px solid #ccc', outline: error ? '2px solid #e74c3c' : undefined }}
                disabled={loading}
            />
            {/* Only show info icon if showInfoIcon is true */}
            {showInfoIcon && (
                <div style={{ position: 'relative', display: 'inline-block', marginLeft: 10 }}>
                    <span
                        ref={infoIconRef}
                        style={{
                            display: 'inline-block',
                            width: 22,
                            height: 22,
                            borderRadius: '50%',
                            background: 'transparent',
                            color: COLORS.background,
                            textAlign: 'center',
                            lineHeight: '22px',
                            fontWeight: 700,
                            fontSize: 16,
                            cursor: 'pointer',
                            userSelect: 'none'
                        }}
                        tabIndex={0}
                        onMouseEnter={() => setShowTooltip(true)}
                        onMouseLeave={() => setShowTooltip(false)}
                        onFocus={() => setShowTooltip(true)}
                        onBlur={() => setShowTooltip(false)}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={COLORS.focusBorder} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10" />
                            <line x1="12" y1="16" x2="12" y2="12" />
                            <line x1="12" y1="8" x2="12.01" y2="8" />
                        </svg>
                    </span>
                    {showTooltip && infoIconRef.current && createPortal(
                        (() => {
                            const rect = infoIconRef.current!.getBoundingClientRect();
                            return (
                                <div
                                    style={{
                                        width: 240,
                                        background: 'rgba(30, 30, 40, 255)',
                                        color: 'rgba(255, 255, 255, 255)',
                                        textAlign: 'left',
                                        borderRadius: 6,
                                        padding: '10px 16px',
                                        position: 'fixed',
                                        zIndex: 99999,
                                        left: rect.right + 8,
                                        top: rect.top - 4,
                                        boxShadow: COLORS.boxShadow,
                                        fontSize: 15,
                                        fontWeight: 400,
                                        pointerEvents: 'auto',
                                        whiteSpace: 'normal',
                                    }}
                                >
                                    {infoText}
                                </div>
                            );
                        })(),
                        document.body
                    )}
                </div>
            )}
            {/* Placeholder div to maintain consistent spacing when no info icon */}
            {!showInfoIcon && (
                <div style={{ width: 80, marginLeft: 10 }} />
            )}
            {/* Toggle slider button, only if toggle/onToggle provided */}
            {typeof toggle === 'boolean' && onToggle && (
                <div style={{ position: 'relative', display: 'inline-block', marginLeft: 10 }}>
                    <button
                        ref={toggleRef}
                        onClick={() => {
                            setIsToggleAnimating(true);
                            onToggle();
                        }}
                        style={{
                            background: toggle ? COLORS.focusBorder : '#888',
                            color: COLORS.background,
                            border: 'none',
                            borderRadius: 12,
                            width: 36,
                            height: 24,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: toggle ? 'flex-end' : 'flex-start',
                            padding: 2,
                            transition: 'background 0.2s',
                        }}
                        aria-label={toggle ? 'Hide right map' : 'Show right map'}
                        className={isToggleAnimating ? 'icon-collapse-animate' : ''}
                        onAnimationEnd={() => setIsToggleAnimating(false)}
                        onMouseEnter={() => setShowToggleTooltip(true)}
                        onMouseLeave={() => setShowToggleTooltip(false)}
                        onFocus={() => setShowToggleTooltip(true)}
                        onBlur={() => setShowToggleTooltip(false)}
                    >
                        <span style={{
                            display: 'inline-block',
                            width: 18,
                            height: 18,
                            borderRadius: '50%',
                            background: COLORS.background,
                            boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                            transition: 'transform 0.2s',
                        }} />
                    </button>
                    {showToggleTooltip && toggleRef.current && createPortal(
                        (() => {
                            const rect = toggleRef.current!.getBoundingClientRect();
                            return (
                                <div
                                    style={{
                                        width: 240,
                                        background: 'rgba(30, 30, 40, 255)',
                                        color: 'rgba(255, 255, 255, 255)',
                                        textAlign: 'left',
                                        borderRadius: 6,
                                        padding: '10px 16px',
                                        position: 'fixed',
                                        zIndex: 99999,
                                        left: rect.right + 8,
                                        top: rect.top - 4,
                                        boxShadow: COLORS.boxShadow,
                                        fontSize: 15,
                                        fontWeight: 400,
                                        pointerEvents: 'auto',
                                        whiteSpace: 'normal',
                                    }}
                                >
                                    {toggle ? 'Hide right globe.' : 'Show right globe for comparison.'}
                                </div>
                            );
                        })(),
                        document.body
                    )}
                </div>
            )}
        </div>
    );
}; 